import React from 'react';
import type Immutable from 'immutable';
import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { BrandKit } from 'js/src/client';
import { NarrativeThreadEnum, WorkspaceMediaModeEnum } from 'js/src/client';
import ColorDots from 'js/src/components/common/ColorDots';
import BadgeButton, { DropdownBadgeButton } from 'js/src/components/common/ui/BadgeButton';
import { type DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import { buildDurationDropdownItems } from 'js/src/components/sketch/sketch-duration-utils';
import { buildFormatDropdownItems } from 'js/src/components/sketch/sketch-format-utils';
import type {
    ClearableSketchControl,
    SketchControl,
} from 'js/src/components/sketch/SketchControls';
import { SKETCH_CONTROL_ICON } from 'js/src/components/sketch/SketchControls';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import { trackUpsellClicked } from 'js/src/libs/analytics-utils';
import { ImmutableFromJS, type ImmutableMap } from 'js/src/libs/consts';
import BlueprintsIcon from 'js/src/libs/custom_icons/BlueprintsIcon';
import { useAppDispatch } from 'js/src/libs/RootState';
import { getNaturalDurationFromSeconds } from 'js/src/libs/video-duration-tools';

export enum BADGE_SIZES {
    SM = 'sm',
    MD = 'md',
}

export interface MediaSourcesState {
    stockMediaEnabled: boolean;
    damMediaEnabled: boolean;
    workspaceMediaMode: WorkspaceMediaModeEnum;
    mediaGuidance: string;
    isDamAvailable: boolean;
}

export interface SketchControlsBadgesState {
    blueprintName: string | null;
    templateName: string | null;
    templateColors: string[] | null;
    narrativeThread: NarrativeThreadEnum | null;
    durationSeconds: number | null;
    aspectRatio: AspectRatio | null;
    voiceName: string | null;
    mediaSources: MediaSourcesState | null;
    brandKitName: string | null;
    brandKitColors: string[] | null;
}

const renderNameWithColors = (name: string, colors: string[] | null): React.ReactNode => {
    if (!colors || colors.length === 0) return name;
    return (
        <>
            {name}
            <ColorDots colors={colors} className="ms-l5-8" />
        </>
    );
};

export interface SketchControlsBadgesProps {
    state: SketchControlsBadgesState;
    size?: BADGE_SIZES;
    disabled?: boolean;
    hideDuration?: boolean;
    supportedAspectRatios?: AspectRatio[];
    brandKits: Immutable.List<ImmutableMap<BrandKit>>;
    onClear: (type: ClearableSketchControl) => void;
    onEdit: (type: SketchControl) => void;
    onSelectDuration: (seconds: number) => void;
    onSelectFormat: (aspectRatio: AspectRatio) => void;
    onSelectBrandKit: (brandKitId: number) => void;
}

const DEFAULT_EMPTY_GUIDANCE = 'Region: any\nEthnicity: any';
const isGuidanceEmpty = (guidance: string): boolean => {
    return guidance.trim() === DEFAULT_EMPTY_GUIDANCE || guidance.trim() === '';
};

const getNarrativeThreadLabel = (narrativeThread: NarrativeThreadEnum): string => {
    switch (narrativeThread) {
        case NarrativeThreadEnum.VOICEOVER:
            return 'Voiceover';
        case NarrativeThreadEnum.TEXT:
            return 'No voiceover';
        case NarrativeThreadEnum.TALKING_HEAD:
            return 'Avatar';
        default:
            return '';
    }
};

const getNarrativeThreadIcon = (narrativeThread: NarrativeThreadEnum): string => {
    switch (narrativeThread) {
        case NarrativeThreadEnum.VOICEOVER:
            return 'record_voice_over';
        case NarrativeThreadEnum.TEXT:
            return 'voice_over_off';
        case NarrativeThreadEnum.TALKING_HEAD:
            return 'person_book';
        default:
            return 'article';
    }
};

const getMediaSourcesLabel = (mediaSources: MediaSourcesState): string => {
    const enabledSources: string[] = [];
    if (mediaSources.stockMediaEnabled) enabledSources.push('Stock');
    if (mediaSources.damMediaEnabled) enabledSources.push('DAM');
    if (mediaSources.workspaceMediaMode !== WorkspaceMediaModeEnum.OFF) {
        enabledSources.push('Workspace');
    }
    const sourcesLabel =
        enabledSources.length === 0 ? 'No recommended media' : enabledSources.join(' + ');
    const hasGuidance = !isGuidanceEmpty(mediaSources.mediaGuidance);
    return hasGuidance ? `${sourcesLabel} (guided)` : sourcesLabel;
};

export const isMediaSourcesDefault = (mediaSources: MediaSourcesState): boolean => {
    // Defaults: stock=true, dam=true (if available), workspace=OFF, guidance=''
    const isStockDefault = mediaSources.stockMediaEnabled === true;
    const isDamDefault = mediaSources.damMediaEnabled === mediaSources.isDamAvailable;
    const isWorkspaceDefault = mediaSources.workspaceMediaMode === WorkspaceMediaModeEnum.OFF;
    const isGuidanceDefault = isGuidanceEmpty(mediaSources.mediaGuidance);

    return isStockDefault && isDamDefault && isWorkspaceDefault && isGuidanceDefault;
};

const getVoiceoverLabel = (
    narrativeThread: NarrativeThreadEnum,
    voiceName: string | null,
): string => {
    if (narrativeThread === NarrativeThreadEnum.VOICEOVER) {
        return voiceName || 'Voiceover: Auto';
    }
    return getNarrativeThreadLabel(narrativeThread);
};

export const SketchControlsBadges: React.FC<SketchControlsBadgesProps> = ({
    state,
    size = BADGE_SIZES.MD,
    disabled = false,
    hideDuration = false,
    supportedAspectRatios,
    brandKits,
    onClear,
    onEdit,
    onSelectDuration,
    onSelectFormat,
    onSelectBrandKit,
}) => {
    const dispatch = useAppDispatch();

    const durationItemPropsList = React.useMemo(() => {
        if (!state.durationSeconds) return null;
        const items = buildDurationDropdownItems(
            (seconds, isLocked) => ({
                seconds: isLocked ? `locked-${seconds}` : seconds,
            }),
            state.durationSeconds,
        );
        return ImmutableFromJS(items) as Immutable.List<
            DropdownItemProp<{ seconds: number | string }>
        >;
    }, [state.durationSeconds]);

    const handleDurationSelect = React.useCallback(
        (data: ImmutableMap<{ seconds: number | string }>) => {
            const value = data?.get?.('seconds');
            if (typeof value === 'string' && String(value).startsWith('locked-')) {
                trackUpsellClicked('duration_dropdown_locked_option');
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: ImmutableFromJS({
                            featureName: LOCKED_FEATURES.MAX_VIDEO_DURATION_LIMIT,
                        }),
                    }),
                );
            } else if (typeof value === 'number' && value > 0) {
                onSelectDuration(value);
            }
        },
        [dispatch, onSelectDuration],
    );

    const formatItemPropsList = React.useMemo(() => {
        if (!state.aspectRatio) return null;
        const availableRatios = supportedAspectRatios || VideoData.ALL_ASPECT_RATIOS;
        const items = buildFormatDropdownItems(
            (aspectRatio) => ({ aspectRatio }),
            availableRatios,
            state.aspectRatio,
        );
        return ImmutableFromJS(items) as Immutable.List<
            DropdownItemProp<{ aspectRatio: AspectRatio }>
        >;
    }, [state.aspectRatio, supportedAspectRatios]);

    const handleFormatSelect = React.useCallback(
        (data: ImmutableMap<{ aspectRatio: AspectRatio }>) => {
            const value = data?.get?.('aspectRatio');
            if (value) {
                onSelectFormat(value);
            }
        },
        [onSelectFormat],
    );

    const brandKitItemPropsList = React.useMemo(() => {
        if (!state.brandKitName || brandKits.size === 0) return null;
        const items = brandKits
            .map((brandKit) => ({
                id: `brand-kit-${brandKit.get('id')}`,
                text: brandKit.get('name'),
                onSelectData: { brandKitId: brandKit.get('id') },
            }))
            .toArray();
        return ImmutableFromJS(items) as Immutable.List<DropdownItemProp<{ brandKitId: number }>>;
    }, [state.brandKitName, brandKits]);

    const handleBrandKitSelect = React.useCallback(
        (data: ImmutableMap<{ brandKitId: number }>) => {
            const value = data?.get?.('brandKitId');
            if (value != null) {
                onSelectBrandKit(value);
            }
        },
        [onSelectBrandKit],
    );

    const showMediaSourcesBadge = state.mediaSources && !isMediaSourcesDefault(state.mediaSources);

    const hasBadges =
        !!state.blueprintName ||
        !!state.templateName ||
        !!state.narrativeThread ||
        !!state.durationSeconds ||
        !!state.aspectRatio ||
        showMediaSourcesBadge ||
        !!state.brandKitName;

    if (!hasBadges) {
        return null;
    }

    return (
        <>
            {state.blueprintName && (
                <BadgeButton
                    closeable
                    disabled={disabled}
                    icon={<BlueprintsIcon width={20} height={20} />}
                    label={state.blueprintName}
                    onClose={() => onClear('blueprint')}
                    onClick={() => onEdit('blueprint')}
                />
            )}

            {state.templateName && (
                <BadgeButton
                    closeable
                    disabled={disabled}
                    icon={SKETCH_CONTROL_ICON.template}
                    label={renderNameWithColors(state.templateName, state.templateColors)}
                    onClose={() => onClear('template')}
                    onClick={() => onEdit('template')}
                />
            )}

            {state.narrativeThread && (
                <BadgeButton
                    closeable
                    disabled={disabled}
                    icon={getNarrativeThreadIcon(state.narrativeThread)}
                    label={getVoiceoverLabel(state.narrativeThread, state.voiceName)}
                    onClose={() => onClear('narrativeThread')}
                    onClick={
                        state.narrativeThread === NarrativeThreadEnum.VOICEOVER
                            ? () => onEdit('voice')
                            : undefined
                    }
                />
            )}

            {!hideDuration && state.durationSeconds && durationItemPropsList && (
                <DropdownBadgeButton
                    closeable
                    disabled={disabled}
                    icon={SKETCH_CONTROL_ICON.duration}
                    label={getNaturalDurationFromSeconds(state.durationSeconds)}
                    onClose={() => onClear('duration')}
                    itemPropsList={durationItemPropsList}
                    onSelect={handleDurationSelect}
                    breakOverflow={true}
                    limitMenuHeight={true}
                />
            )}

            {state.aspectRatio && formatItemPropsList && (
                <DropdownBadgeButton
                    closeable
                    disabled={disabled}
                    icon={SKETCH_CONTROL_ICON.format}
                    label={VideoData.getAspectRatioDisplayName(state.aspectRatio)}
                    onClose={() => onClear('format')}
                    itemPropsList={formatItemPropsList}
                    onSelect={handleFormatSelect}
                    breakOverflow={true}
                />
            )}
            {showMediaSourcesBadge && state.mediaSources && (
                <BadgeButton
                    closeable
                    disabled={disabled}
                    icon={SKETCH_CONTROL_ICON.mediaSources}
                    label={getMediaSourcesLabel(state.mediaSources)}
                    onClose={() => onClear('mediaSources')}
                    onClick={() => onEdit('mediaSources')}
                />
            )}

            {state.brandKitName && brandKitItemPropsList && (
                <DropdownBadgeButton
                    closeable
                    disabled={disabled}
                    icon={SKETCH_CONTROL_ICON.brandKit}
                    label={renderNameWithColors(state.brandKitName, state.brandKitColors)}
                    onClose={() => onClear('brandKit')}
                    itemPropsList={brandKitItemPropsList}
                    onSelect={handleBrandKitSelect}
                    breakOverflow={true}
                />
            )}
        </>
    );
};
